import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

// material-ui
import {
    Avatar,
    Box,
    IconButton,
    Tooltip,
    useMediaQuery,
    useTheme,
    Container,
    Typography
} from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import QuestionMarkOutlinedIcon from '@mui/icons-material/QuestionMarkOutlined';
import { filterDataByDate } from 'utils/dates';

// project imports
import useConfig from 'hooks/useConfig';
import { useDispatch, useSelector } from 'store';
import LAYOUT_CONST from 'constant';
import LogoSection from '../LogoSection';
import ProfileSection from './ProfileSection';
import useAPI from 'hooks/useAPI';
import ProjectAndTaskService from 'services/ProjectAndTaskService';
import { openDrawer } from 'store/slices/menu';
import PunchClock from './PunchClock';
import { EMPLOYER_ROLE, ADMIN_ROLE, EMPLOYEE_ROLE, CS_TECHNICAL_ROLE, CS_OTHERS_ROLE, CA_USER } from 'config';
import useAuth from 'hooks/useAuth';
import { getProjects } from 'store/slices/timesheet';
import { sanitizeProjects } from 'utils/sanitization';
import TimerBar from 'jsx/components/extended/TimeBar';
import NotificationModule from './NotiicationModdule';
import UserManualPopup from './ManualModule';
// assets
import { IconMenu2 } from '@tabler/icons';
import { format } from 'date-fns';

// ==============================|| MAIN NAVBAR / HEADER ||============================== //

const Header = () => {

    const theme = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const { isLoggedIn, user } = useAuth();
    const { drawerOpen } = useSelector((state) => state.menu);
    const { layout } = useConfig();
    const company_uuid = useSelector((state) => state.user.profile.companyUUID);
    const empoyee_uuid = useSelector((state) => state.user.profile.employeeUUID);
    const { actAs } = useSelector((state) => state.user);
    // const selectTimeEntry = (state) => state.timesheet.timeEntry;

    const timeEntryData = useSelector((state) => state.timesheet.timeEntry);
    const currentDate = new Date();
    const todayDate = format(currentDate, 'yyyy-MM-dd')
    const filteredData = useMemo(() => filterDataByDate(timeEntryData, todayDate), [timeEntryData, todayDate]);

    const filteredTasks = filteredData.filter((task) => {
        if (
            (task.timeentryOrigin === 'week' || task.timeentryOrigin === 'na') &&
            task.durationInSeconds <= 1
        ) {
            return false;
        }
        if (task.entryStatus === 'rejected' || task.type === 'leave-entry') {
            return false;
        }
        return true;
    });


    const activeTimerTask = useSelector((state) => state.timesheet.currentTask);
    const checkTimerTask = Object.keys(activeTimerTask).length === 0;

    const [totalDuration, setTotalDuration] = useState('');

    const formatSecondsToHoursAndMinutes = (seconds) => {
        const hours = String(Math.floor(seconds / 3600)).padStart(2, '0');
        const minutes = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
        const remainingSeconds = String(seconds % 60).padStart(2, '0');

        return `${hours}:${minutes}:${remainingSeconds}`;
    };




    const handleClickSupport = () => {
        if (isLoggedIn) {
            if (user.role === EMPLOYER_ROLE) {
                navigate('/employer/support');
            }
            if (user.role === CA_USER) {
                navigate('/ca/support');
            } if (user.role === EMPLOYEE_ROLE) {
                navigate('/employee/support');
            }
        }
    };


    function handleProjectsSuccess(response) {
        const projects = sanitizeProjects(response.data.items);
        dispatch(getProjects(projects));
    }

    const projectsList = useAPI(ProjectAndTaskService.listProjects, handleProjectsSuccess);

    useEffect(() => {
        if (user && user.role != EMPLOYER_ROLE && user.role != CS_TECHNICAL_ROLE && user.role != CS_OTHERS_ROLE && user.role !== CA_USER) {
            projectsList.execute(company_uuid);
        }
    }, [])

    useEffect(() => {
        if (filteredTasks.length) {
            // const today = moment().format('YYYY-MM-DD');
            // const todayEntries = filteredData.filter((entry) => moment(entry.createdAt, 'YYYY-MM-DD HH:mm:ss').isSame(today, 'day'));
            const totalDurationInSeconds = filteredTasks
                .map((entry) => entry.durationInSeconds)
                .reduce((total, seconds) => total + seconds, 0);
            const formattedTotalDuration = formatSecondsToHoursAndMinutes(totalDurationInSeconds);
            setTotalDuration(formattedTotalDuration);
        } else {
            // Reset durations if no entries
            setTotalDuration('00:00:00');
        }
    }, [filteredTasks]);


    return (
        <>
            {/* logo & toggler button */}
            <Box
                sx={{
                    width: 228,
                    display: 'flex',
                    [theme.breakpoints.down('md')]: {
                        width: 'auto'
                    }
                }}
            >
                <Box component="span" sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
                    <LogoSection />
                </Box>

                {layout === LAYOUT_CONST.VERTICAL_LAYOUT || (layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && matchDownMd) ? (
                    <Avatar
                        variant="rounded"
                        sx={{
                            ...theme.typography.commonAvatar,
                            ...theme.typography.mediumAvatar,
                            overflow: 'hidden',
                            transition: 'all .2s ease-in-out',
                            background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.secondary.light,
                            color: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.secondary.dark,
                            '&:hover': {
                                background: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.secondary.dark,
                                color: theme.palette.mode === 'dark' ? theme.palette.secondary.light : theme.palette.secondary.light
                            }
                        }}
                        onClick={() => dispatch(openDrawer(!drawerOpen))}
                        color="inherit"
                    >
                        <IconMenu2 stroke={1.5} size="20px" />
                    </Avatar>
                ) : null}
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {actAs && actAs === 'employer' ? <Box sx={{ alignItems: 'center', display: { md: 'flex', lg: 'flex' } }}>
                <TimerBar />
            </Box> : <></>}

            <Box sx={{ flexGrow: 2 }} />

            {/* notification, profile, timer */}
            {user && user.role != EMPLOYER_ROLE && user.role != ADMIN_ROLE && user.role != CS_TECHNICAL_ROLE && user.role != CS_OTHERS_ROLE && user.role !== CA_USER ? (
                <>
                    {checkTimerTask ? <Typography variant="h4" sx={{ marginRight: 3 }}>
                        {/* <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{`Last Punch Time `}&nbsp;</span>&nbsp; */}
                        ({totalDuration || '-'})
                    </Typography> : <></>}

                    <PunchClock />
                </>
            ) : null}
            {
                user.role != ADMIN_ROLE && user.role != EMPLOYEE_ROLE && user.role != CS_TECHNICAL_ROLE && user.role != CS_OTHERS_ROLE ? (
                    <UserManualPopup />
                ) : null
            }
            {
                user.role != ADMIN_ROLE && user.role != EMPLOYEE_ROLE && user.role != CS_TECHNICAL_ROLE && user.role != CS_OTHERS_ROLE ? (
                    <Box
                        sx={{
                            // width: 228,
                            marginRight: '10px',
                            display: 'flex',
                            [theme.breakpoints.down('md')]: {
                                width: 'auto'
                            }
                        }}
                    >
                        <Avatar
                            variant="rounded"
                            sx={{
                                ...theme.typography.commonAvatar,
                                ...theme.typography.mediumAvatar,
                                overflow: 'hidden',
                                transition: 'all .2s ease-in-out',
                                background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.secondary.light,
                                '&:hover': {
                                    background: theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.dark,
                                }
                            }}
                            color="inherit"
                        >
                            <Tooltip title="Support">
                                <IconButton onClick={handleClickSupport}>
                                    <SupportAgentIcon color="inherit" stroke={1.5} size="20px" />
                                </IconButton>
                            </Tooltip>
                        </Avatar>
                    </Box>
                ) : null
            }
            {
                (user.role === EMPLOYEE_ROLE || user.role === EMPLOYER_ROLE) && <NotificationModule company_uuid={company_uuid} empoyee_uuid={empoyee_uuid} />
            }

            <ProfileSection user={user} />
        </>
    );

};

export default Header;
